import { useEffect, useRef, useState } from "react";
import { Search, X, LocateFixed, Loader2, MapPin } from "lucide-react";
import { geocode, parseCoords } from "../lib/geocode";

export const MapSearch = ({ getCenter, onSelect, onLocate }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const ctrl = useRef(null);
  const skip = useRef("");

  useEffect(() => { const on = () => setOnline(navigator.onLine); window.addEventListener("online", on); window.addEventListener("offline", on); return () => { window.removeEventListener("online", on); window.removeEventListener("offline", on); }; }, []);

  useEffect(() => {
    ctrl.current?.abort(); setError("");
    const text = q.trim();
    if (text.length < 3 || parseCoords(text) || text === skip.current) { setResults([]); setBusy(false); return; }
    const c = new AbortController(); ctrl.current = c;
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { results: r } = await geocode(text, getCenter?.(), c.signal); if (!c.signal.aborted) { setResults(r); setActive(0); setOpen(true); } }
      catch (e) { if (e.name !== "AbortError") setError("Search unavailable — try again or tap the map"); }
      if (!c.signal.aborted) setBusy(false);
    }, 350);
    return () => { clearTimeout(t); c.abort(); };
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (r) => { onSelect({ lat: +r.lat.toFixed(6), lng: +r.lng.toFixed(6), label: r.label }); skip.current = r.name; setQ(r.name); setOpen(false); setResults([]); };
  const submit = () => {
    const c = parseCoords(q);
    if (c) { onSelect({ ...c, label: "" }); setOpen(false); return; }
    if (results[active]) choose(results[active]);
  };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); submit(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Escape") setOpen(false);
  };
  const coords = parseCoords(q);

  return (
    <div className="map-search notranslate" translate="no" data-testid="map-search">
      <div className={`map-search-box ${!online ? "opacity-70" : ""}`}>
        {busy ? <Loader2 size={18} className="animate-spin text-primary" /> : <Search size={18} className="text-muted" />}
        <input className="flex-1 bg-transparent outline-none min-w-0" placeholder={online ? "Search address or place…" : "Search needs internet — tap the map to drop a pin"} value={q} disabled={!online}
          onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)} onKeyDown={onKey} aria-label="Search address or place" autoComplete="off" data-testid="map-search-input" />
        {q && <button type="button" className="icon-btn-sm" onClick={() => { setQ(""); setResults([]); setOpen(false); }} aria-label="Clear search" data-testid="map-search-clear"><X size={16} /></button>}
      </div>
      {!online && <p className="map-search-hint" data-testid="map-search-offline">Search needs internet — tap the map to drop a pin</p>}
      {open && online && (
        <div className="map-search-panel" data-testid="map-search-results" role="listbox">
          {coords && <button type="button" className="map-search-item" onClick={submit} data-testid="map-search-coords"><MapPin size={16} className="text-primary" /><span><span className="font-semibold">Go to {coords.lat}, {coords.lng}</span><span className="block text-xs text-muted">Pasted coordinates</span></span></button>}
          {results.map((r, i) => (
            <button type="button" key={r.id} className={`map-search-item ${i === active ? "active" : ""}`} role="option" aria-selected={i === active} onMouseEnter={() => setActive(i)} onClick={() => choose(r)} data-testid={`map-search-result-${i}`}>
              <MapPin size={16} className="text-primary shrink-0" /><span className="min-w-0"><span className="font-semibold block truncate notranslate" translate="no">{r.name}</span>{r.secondary && <span className="block text-xs text-muted truncate">{r.secondary}</span>}</span>
            </button>
          ))}
          {error && <p className="px-3 py-2 text-xs text-[var(--safflower)]" data-testid="map-search-error">{error}</p>}
          {!results.length && !coords && !error && q.trim().length >= 3 && !busy && <p className="px-3 py-2 text-xs text-muted" data-testid="map-search-empty">No places found</p>}
          <button type="button" className="map-search-item" onClick={() => { setOpen(false); onLocate(); }} data-testid="map-search-locate"><LocateFixed size={16} className="text-primary" /><span className="font-semibold">Use my location</span></button>
          <p className="map-search-footer" data-testid="map-search-attribution">© OpenStreetMap contributors</p>
        </div>
      )}
    </div>
  );
};
